/**
 * formato.js — formato de dinero y fechas para las pantallas de YoolPay.
 *
 * POR QUÉ NO SE USA `src/styles/tokens/formatters.js`
 * ---------------------------------------------------
 * Existe y tiene `formatCLP` / `formatDate`, pero las dos son incorrectas para
 * este dominio, y no por estilo: por plata.
 *
 *  · `formatCLP(null)` devuelve `'$0'`. En YoolPay un monto ausente NO es cero:
 *    es una cuota pactada en UF sin valor de UF para su fecha, que payment deja
 *    sin valorizar a propósito (fail-closed, ADR-003). Pintar `$0` dice "no hay
 *    nada que cobrar" cuando la verdad es "no sabemos cuánto todavía" —lo
 *    contrario— y ese total se usaría para decidir caja. Aquí un monto ausente es
 *    `'—'`, y si es un piso se marca con `≥`.
 *
 *  · `formatDate('2026-08-02')` hace `new Date(v)` y lo imprime con
 *    `timeZone: 'America/Santiago'`. Sobre un `'YYYY-MM-DD'` eso parsea
 *    medianoche UTC y la muestra en UTC−4, o sea IMPRIME EL DÍA ANTERIOR. En
 *    pantallas cuyo eje es el día, correrse uno es fatal.
 *
 * EL CORTE DEL DÍA ES UTC, NO CHILE
 * ---------------------------------
 * `payment` selecciona las cuotas por `charge_date` con el reloj UTC del
 * servidor (`ufOracleToday`), el mismo corte con que el ciclo de cobro decide
 * qué cobrar. `formatters.js` corta en hora de Chile. Durante parte de la jornada
 * son días DISTINTOS: usar el corte de Chile abre un día que no es el que cobró
 * el worker. Por eso las fechas de aquí viven en UTC.
 *
 * LAS DOS FAMILIAS DE FECHA, SEPARADAS A PROPÓSITO
 * ------------------------------------------------
 * Son dos cosas distintas que se confunden fácil, así que van como funciones
 * distintas y documentadas en vez de una con un flag:
 *
 *  · `fmtFechaIso`  — un `'YYYY-MM-DD'`, o sea un DÍA calendario. UTC, sin
 *    corrimiento. La usan la jornada y las cuotas.
 *  · `fmtFechaHora` — un `timestamptz`, o sea un INSTANTE. Hora de Chile, que
 *    para un instante es lo correcto: "cuándo pasó" se lee en la zona de quien
 *    mira. La usan `created_at`, `odoo_synced_at` y las firmas de la bitácora.
 *
 * @see src/styles/tokens/formatters.js — el módulo general, inseguro para este dominio
 * @see src/lib/yoolpay/estados.js — el otro módulo compartido de estas pantallas
 */

const LOCALE = 'es-CL';

/** Zona de Chile. Solo para INSTANTES (timestamptz), nunca para un día calendario. */
const TZ_CHILE = 'America/Santiago';

// ---------------------------------------------------------------------------
// Validación de entrada
// ---------------------------------------------------------------------------

/** Mismo formato que valida payment en `GET /api/dia`. */
export const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Mismo formato que valida payment en `GET /api/settlement`. */
export const PERIODO_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// ---------------------------------------------------------------------------
// Dinero
// ---------------------------------------------------------------------------

/**
 * Monto en pesos. Un valor ausente es `'—'`, NUNCA `'$0'`.
 *
 * La diferencia entre `'—'` y `'$0'` es la diferencia entre "no sabemos" y "no
 * hay nada", y en una pantalla de recaudación eso decide si alguien va a buscar
 * plata o se queda tranquilo.
 */
export const fmtClp = (v) =>
  v != null && !Number.isNaN(Number(v))
    ? new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(v)
    : '—';

/**
 * Monto que puede ser un PISO en vez de un total.
 *
 * Cuando payment responde `estimacion_completa === false` quedan cuotas en UF sin
 * valor de UF para su fecha: el monto que trae es lo que se pudo valorizar, no el
 * total. Se muestra con el prefijo `≥` pegado al número.
 *
 * Y si no hay monto valorizado dice `'sin valorizar'`, no `'$0'`: un cero ahí se
 * lee como "este día no cobró nada", exactamente lo contrario de lo que pasa
 * (hay cuotas, no sabemos por cuánto).
 *
 * @param {number|null} v         monto valorizado
 * @param {boolean}     completa  `estimacion_completa` de payment
 */
export const fmtClpPiso = (v, completa) => {
  if (completa !== false) return fmtClp(v);
  if (v == null || Number(v) === 0) return 'sin valorizar';
  return `≥ ${fmtClp(v)}`;
};

/** Cifra sin unidad (cuotas, asientos, transacciones). Ausente es `'—'`. */
export const fmtNum = (v) =>
  v != null && !Number.isNaN(Number(v)) ? new Intl.NumberFormat(LOCALE).format(v) : '—';

/**
 * Monto con signo explícito, para diferencias.
 *
 * Una diferencia sin signo no sirve: `$148.700` no dice si sobra o falta plata, y
 * quién le debe a quién es justamente el dato. El cero va como `'$0'` real porque
 * ahí sí significa cero: la diferencia es nula.
 */
export const fmtDelta = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (n === 0) return fmtClp(0);
  return `${n > 0 ? '+' : '−'}${fmtClp(Math.abs(n))}`;
};

// ---------------------------------------------------------------------------
// Fechas — día calendario (UTC)
// ---------------------------------------------------------------------------

/**
 * `'2026-08-02'` → `'02/08/2026'`.
 *
 * Parte el string a mano en vez de construir un `Date`, así no hay zona horaria
 * que pueda correr el día. Es la razón de ser de esta función.
 */
export const fmtFechaIso = (v) => {
  if (!v || !FECHA_RE.test(v)) return '—';
  const [y, m, d] = v.split('-');
  return `${d}/${m}/${y}`;
};

/** `'2026-08-02'` → `'2 de agosto de 2026'`. UTC, sin corrimiento. */
export const fmtFechaLarga = (v) => {
  if (!v || !FECHA_RE.test(v)) return '—';
  const t = new Date(`${v}T00:00:00Z`);
  if (Number.isNaN(t.getTime())) return '—';
  return t.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * `'2026-08-07'` → `'7 de agosto'`. Sin año, UTC.
 *
 * Para listas que viven DENTRO de un período ya rotulado —la cola de días con
 * pendientes cuelga del título «agosto 2026»—, donde repetir el año en cada fila
 * es ruido que además empuja el motivo a una línea más.
 */
export const fmtDiaMes = (v) => {
  if (!v || !FECHA_RE.test(v)) return '—';
  const t = new Date(`${v}T00:00:00Z`);
  if (Number.isNaN(t.getTime())) return '—';
  return t.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', timeZone: 'UTC' });
};

/** Día de la semana de un `'YYYY-MM-DD'`. `timeZone: 'UTC'` por lo mismo. */
export const fmtDiaSemana = (v) => {
  if (!v || !FECHA_RE.test(v)) return '';
  const t = new Date(`${v}T00:00:00Z`);
  if (Number.isNaN(t.getTime())) return '';
  return t.toLocaleDateString(LOCALE, { weekday: 'long', timeZone: 'UTC' });
};

/**
 * Hoy según el corte UTC — el mismo con que el ciclo de cobro elige las cuotas.
 *
 * NO es `todayISOChile()` de `formatters.js`: ésa corta en hora de Chile y
 * durante parte del día devuelve otra fecha, abriendo una jornada distinta de la
 * que cobró el worker.
 */
export const hoyUtc = () => new Date().toISOString().slice(0, 10);

/** Suma días a un `'YYYY-MM-DD'` sin salirse de UTC. */
export const sumarDias = (fecha, n) => {
  if (!FECHA_RE.test(fecha)) return fecha;
  const t = new Date(`${fecha}T00:00:00Z`);
  if (Number.isNaN(t.getTime())) return fecha;
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

/** Cuántos días tiene un período `'YYYY-MM'`. */
export const diasDelPeriodo = (periodo) => {
  if (!PERIODO_RE.test(periodo)) return 0;
  const [y, m] = periodo.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
};

/** Día de la semana (0 = domingo) del día 1 de un período, para alinear el calendario. */
export const primerDiaSemana = (periodo) => {
  if (!PERIODO_RE.test(periodo)) return 0;
  const [y, m] = periodo.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
};

/** `('2026-08', 2)` → `'2026-08-02'`. */
export const fechaDe = (periodo, dia) => `${periodo}-${String(dia).padStart(2, '0')}`;

// ---------------------------------------------------------------------------
// Fechas — instante (hora de Chile)
// ---------------------------------------------------------------------------

/**
 * Un `timestamptz` → `'02/08/2026 14:35'`, en hora de Chile.
 *
 * Aquí la zona de Chile SÍ es lo correcto: es un instante, y "cuándo pasó" se lee
 * en la zona de quien mira. Se usa para `created_at`, `odoo_synced_at` y las
 * firmas de la bitácora — nunca para un `charge_date`.
 */
export const fmtFechaHora = (v) => {
  if (!v) return '—';
  const t = new Date(v);
  if (Number.isNaN(t.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ_CHILE,
  }).format(t);
};

/**
 * Sólo la hora de un instante: `'18:30'`.
 *
 * Para la lista de transacciones del día, donde la fecha ya la da la pantalla y
 * repetirla en 200 filas es ruido. Misma zona que `fmtFechaHora` y por lo mismo:
 * es un instante, y «cuándo pasó» se lee en la zona de quien mira.
 *
 * `'—'` cuando no hay: una cuota que no se cobró no tiene hora, y un `'00:00'`
 * diría que se cobró a medianoche.
 */
export const fmtHora = (v) => {
  if (!v) return '—';
  const t = new Date(v);
  if (Number.isNaN(t.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: TZ_CHILE,
  }).format(t);
};

// ---------------------------------------------------------------------------
// Períodos
// ---------------------------------------------------------------------------

/** `'2026-08-02'` → `'2026-08'`. El período al que pertenece un día. */
export const periodoDe = (fecha) => (fecha && FECHA_RE.test(fecha) ? fecha.slice(0, 7) : null);

/** `'2026-08'` → `'agosto 2026'`, para títulos. */
export const fmtPeriodo = (periodo) => {
  if (!periodo || !PERIODO_RE.test(periodo)) return '—';
  const [y, m] = periodo.split('-').map(Number);
  const nombre = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(LOCALE, {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${nombre} ${y}`;
};

/** El período de hoy, con el corte UTC. */
export const periodoActual = () => hoyUtc().slice(0, 7);

/** Corre un período `'YYYY-MM'` n meses. `('2026-08', -1)` → `'2026-07'`. */
export const sumarMeses = (periodo, n) => {
  if (!PERIODO_RE.test(periodo)) return periodo;
  const [y, m] = periodo.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}`;
};
